import LoadingState from '../../../ReusableComponents/LoadingState';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { CircularProgress } from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import { MdDownload, MdRefresh } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import Request from '../../../../Request';
import { logout } from '../../../../actions/AppActions';
import ResponsiveTable from '../../../ReusableComponents/ResponsiveTable';
import Search from '../../../ReusableComponents/Search';
import './DashboardReports.css';

export default function CandidateAttendanceReport({ faculdade_id, processo_id }) {
    const dispatch = useDispatch();
    const user = useSelector(state => state.AppReducer.user);
    const [data, setData] = useState({ rows: [], summary: {}, pagination: {}, job: null });
    const [search, setSearch] = useState('');
    const [presence, setPresence] = useState('');
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [message, setMessage] = useState('');
    const serial = useRef(0);
    const mounted = useRef(true);
    const scope = new URLSearchParams(processo_id ? { processo_id } : {}).toString();
    const valid = useCallback(response => {
        if (!response) { setMessage('Não foi possível completar a requisição.'); return false; }
        if (response.message === 'Unauthenticated.') { dispatch(logout()); return false; }
        if (response.errors || response.error) {
            setMessage(Object.values(response.errors || { error: response.error }).flat().join(' '));
            return false;
        }
        return true;
    }, [dispatch]);
    const load = useCallback(async () => {
        if (!faculdade_id) return;
        const requestId = ++serial.current;
        const params = new URLSearchParams(scope);
        params.set('page', page);
        if (search) params.set('search', search);
        if (presence) params.set('presenca', presence);
        setLoading(true);
        const response = await Request.Get(`faculdade/get_presence_report/${faculdade_id}?${params}`);
        if (!mounted.current || requestId !== serial.current) return;
        setLoading(false);
        if (valid(response)) setData(response);
    }, [faculdade_id, scope, page, search, presence, valid]);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; serial.current++; };
    }, []);
    useEffect(() => {
        const timer = setTimeout(load, 350);
        return () => { clearTimeout(timer); serial.current++; };
    }, [load]);
    const pending = ['0', '1'].includes(String(data.job?.status));
    useEffect(() => {
        if (!pending) return;
        const timer = setInterval(load, 15000);
        return () => clearInterval(timer);
    }, [pending, load]);
    const download = async () => {
        setExporting(true);
        setMessage('');
        const response = await Request.Post(`faculdade/generate_presence_report/${faculdade_id}?${scope}`, new FormData());
        if (!mounted.current) return;
        if (valid(response)) {
            if (response.can_download) {
                const file = await Request.Get(`faculdade/download_presence_report/${faculdade_id}?${scope}`);
                if (mounted.current && valid(file) && file.path) window.open(file.path, '_blank', 'noopener,noreferrer');
            } else setMessage(response.message || 'Planilha na fila de geração.');
            await load();
        }
        if (mounted.current) setExporting(false);
    };
    const status = { '0': 'Na fila', '1': 'Gerando', '2': data.can_download ? 'Atualizada' : 'Desatualizada', '3': 'Erro na geração' }[String(data.job?.status)];
    return <div className="dashboard-report-card">
        <div className="dashboard-report-header">
            <div>
                <h5>Presentes e ausentes</h5>
                <p>Presente quando existe um resultado de prova do candidato {user?.empresa?.change_text ? 'naquela vaga/cargo' : 'naquele processo seletivo'}. A presença é independente em cada vínculo.</p>
                <div className="dashboard-result-summary">
                    <span className="dashboard-report-badge neutral">{processo_id ? 'Candidatos' : 'Vínculos'}: {data.summary.total || 0}</span>
                    <span className="dashboard-report-badge success">Presentes: {data.summary.presentes || 0}</span>
                    <span className="dashboard-report-badge danger">Ausentes: {data.summary.ausentes || 0}</span>
                </div>
                {status && <span className="dashboard-report-job">Planilha: {status}</span>}
                {message && <div role="status" className="dashboard-report-job">{message}</div>}
                {String(data.job?.status) === '3' && <div role="alert" className="dashboard-report-job">{data.job.error_message}</div>}
            </div>
            <div className="dashboard-report-actions hidethis">
                <Button variant="light" onClick={load} disabled={loading}><MdRefresh /> Atualizar</Button>
                <Button variant="success" onClick={download} disabled={exporting || pending}>
                    {exporting || pending ? <CircularProgress size={16} /> : <MdDownload />}
                    {' '}{pending ? 'Gerando planilha' : 'Baixar planilha completa'}
                </Button>
            </div>
        </div>
        <div className="dashboard-report-filter dashboard-attendance-filters hidethis">
            <div className="dashboard-attendance-search">
                <Form.Label>Buscar candidatos</Form.Label>
                <Search hide_class className="dashboard-report-search" placeholder={user?.empresa?.change_text ? 'Nome, e-mail, CPF ou vaga/cargo' : 'Nome, e-mail, CPF ou processo seletivo'} ValueChange={value => { setPage(1); setSearch(value); }} />
            </div>
            <div className="dashboard-attendance-presence">
                <Form.Label htmlFor="candidate-attendance-presence">Presença</Form.Label>
                <Form.Select id="candidate-attendance-presence" className="form-control" aria-label="Filtrar presença" value={presence} onChange={event => { setPage(1); setPresence(event.target.value); }}>
                    <option value="">Todos os candidatos</option>
                    <option value="PRESENTE">Presentes</option>
                    <option value="AUSENTE">Ausentes</option>
                </Form.Select>
            </div>
        </div>
        <p className="dashboard-report-job">A planilha inclui todos os vínculos {processo_id ? (user?.empresa?.change_text ? 'desta vaga/cargo' : 'deste processo seletivo') : (user?.empresa?.change_text ? 'das vagas/cargos ativos' : 'dos processos seletivos ativos')}, independentemente dos filtros da tabela.</p>
        <ResponsiveTable className="table table-striped">
            <thead><tr><th>Candidato</th><th>E-mail</th><th>CPF</th><th>{user?.empresa?.change_text ? 'Vaga/Cargo' : 'Processo seletivo'}</th><th>Inscrição</th><th>Presença</th></tr></thead>
            <tbody>{data.rows.map(row => <tr key={`${row.processo_id}-${row.user_id}`}>
                <td>{row.name}</td><td>{row.email}</td><td>{row.cpf || '-'}</td><td>{row.processo_name}</td><td>{row.numero_inscricao ?? '-'}</td>
                <td><span className={`dashboard-report-badge ${row.presenca === 'PRESENTE' ? 'success' : 'danger'}`}>{row.presenca}</span></td>
            </tr>)}</tbody>
        </ResponsiveTable>
        {loading && <LoadingState title="Consultando presenças" description="Buscando os presentes e ausentes conforme os filtros." />}
        {!loading && data.rows.length === 0 && <div className="dashboard-report-empty">Nenhum candidato encontrado.</div>}
        {(data.pagination.last_page || 1) > 1 && <div className="dashboard-attendance-pagination hidethis">
            <span>Página <strong>{page}</strong> de <strong>{data.pagination.last_page}</strong></span>
            <Pagination aria-label="Páginas do relatório de presença" color="primary" count={data.pagination.last_page} page={page} disabled={loading} onChange={(_, value) => setPage(value)} />
        </div>}
    </div>;
}
